<script>
import {defineComponent} from "vue";
import Icon from "../components/Icon.vue";
import {useDonationSessionStore} from "../../stores/donationSession";
import {useRoute, useRouter} from "vue-router";
import DonationCheckoutSummary from "../donation/DonationCheckoutSummary.vue";
import Drawer from "../components/Drawer.vue";
import {useI18n} from "vue-i18n";
import HelcimPaymentButton from "../paymentProcessors/HelcimPaymentButton.vue";
import StripePaymentButton from "../paymentProcessors/StripePaymentButton.vue";
import {ApplicationHelper} from "../../helpers/ApplicationHelper";
import DonationTipForm from "../donation/DonationTipForm.vue";
import AccordionItem from "../components/accordion/AccordionItem.vue";
import ThankYouAnimation from "../animations/ThankYou.vue";
import BaseModal from "../components/BaseModal.vue";
import {useAnalyticEventStore} from "../../stores/analyticEvents";
import {useLandingPageStore} from "../../stores/landingPage";
import {useUserStore} from "../../stores/user";
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import Tooltip from "../components/tooltip.vue";
import DonationCheckoutTotal from "../donation/DonationCheckoutTotal.vue";
import ToggleSwitch from "../components/inputs/ToggleSwitch.vue";
import SignInModal from "../components/SignInModal.vue";
import LoadingSpinner from "../components/loadingSpinner.vue";
import Popup from "../components/popup.vue";


export default defineComponent({
  // Not named the best, adding this button will add the whole gift basket / checkout process drawer
  name: "GiftBasketSideBarButton",
  components: {
    Popup,
    LoadingSpinner,
    SignInModal,
    ToggleSwitch,
    DonationCheckoutTotal,
    Tooltip,
    BaseModal,
    ThankYouAnimation,
    AccordionItem,
    DonationTipForm,
    StripePaymentButton,
    HelcimPaymentButton,
    Drawer,
    DonationCheckoutSummary,
    Icon,
    VueDatePicker
  },
  props: {
    disableSidebar: {
      type: Boolean,
      default: false
    },
  },
  emits: ['donation-success', 'close'],
  setup() {
    const router = useRouter();
    const route = useRoute();
    const {t} = useI18n();

    const sessionStore = useDonationSessionStore();
    const landingStore = useLandingPageStore();
    const userStore = useUserStore();
    const analyticEventStore = useAnalyticEventStore();

    return {
      router,
      route,
      t,
      sessionStore,
      landingStore,
      userStore,
      analyticEventStore
    };
  },
  data() {
    return {
      showBasket: false,
      checkingOut: false,
      popupMessage: "",
      validProcessor: false,
      showSignInModal: false,
      userSignedIn: false,
      userSignOutClicked: false,
      signingOut: false,
      signedOut: false
    };
  },
  computed: {
    organizationId() {
      return this.landingStore.organizationId || this.route.params.org;
    },
    ApplicationHelper() {
      return ApplicationHelper;
    },
    formData() {
      return this.sessionStore.donationForm;
    },
    formErrors() {
      return this.sessionStore.donationForm.errors;
    },
    showHelcimPaymentButton() {
      return this.sessionStore.orgProcessorType?.includes("Helcim");
    },
    showStripePaymentButton() {
      return this.sessionStore.orgProcessorType?.includes("Stripe");
    },
    hasDonationInCart() {
      return this.sessionStore.donationCart.length > 0 || this.sessionStore.immediateCheckoutDonation;
    },

    cssProps() {
      if (this.landingStore.localBrand) {
        return {
          "--color-primary": this.landingStore.localBrand.color_primary,
          "--color-primary-dark": this.landingStore.localBrand.color_primary_dark,
          "--color-primary-text": this.landingStore.getContrastTextColor(this.landingStore.localBrand.color_primary)
        };
      }
    },

    isSubscriptionCheckout() {
      return this.sessionStore.immediateCheckoutDonation && this.sessionStore.immediateCheckoutDonation.type === 'RecurringDonation';
    },
    showDelayedSubscriptionToggle() {
      return this.sessionStore.orgProcessor
        && this.sessionStore.orgProcessor.delayed_subscriptions_enabled
        && this.isSubscriptionCheckout;
    },

    // Determine the max date to display in delay donation date picker, based on the selected frequency
    // The user can specify any date up to 6 months in the future to delay the subscription start
    calendarMax() {
      let today = new Date();
      let futureDate = new Date();
      futureDate.setMonth(today.getMonth() + 6);
      return futureDate;
    },

    // Date must be in the future for delayed donations
    calendarMin() {
      let today = new Date();
      let tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);
      return tomorrow;
    },
  },
  watch: {},
  mounted() {
    if(this.disableSidebar) {
      this.validProcessor = true;
    } else {
      const orgId = this.organizationId || this.route.params.org;
      if(orgId) {
        this.sessionStore.loadOrgDonationForm(orgId).then((res) => {
          this.validProcessor = res;
          this.populateDonorInfo(); //populate info if they are signed in (even after they have just given)
        });
      } else {
        this.validProcessor = null;
      }
    }
  },
  methods: {
    checkout() {
      if (this.hasDonationInCart) {
        this.toggleDrawerFullScreen(true);
        this.checkingOut = true;
        this.populateDonorInfo();
        this.analyticEventStore.logEvent({
          action: `gift-basket-sidebar-checkout`
        });
      }
    },

    onPaymentSuccess() {
      this.analyticEventStore.logEvent({
        action: `gift-basket-sidebar-payment-success`
      });
      this.$emit('donation-success', { userSignedIn: this.userSignedIn });
      this.$refs.drawer.hide();
    },

    donateButtonClicked() {
      if (this.sessionStore.donationFormValid()) {
        this.$refs.paymentButton.initPaymentForm();
      }
    },

    onPaymentError(message) {
      console.error('error message');
      if (message && message.includes("payment_processor_unavailable")) {
        this.popupMessage = "The payment processor is currently unavailable. Please try again later or contact the organization.";
      } else {
        this.popupMessage = message || "Something went wrong!";
      }
      this.$refs.popup.show();
      this.analyticEventStore.logEvent({
        action: `gift-basket-sidebar-payment-error`
      });
    },

    closeBasket() {
      this.analyticEventStore.logEvent({
        action: `gift-basket-sidebar-close`
      });
      this.toggleDrawerFullScreen(false);
      this.checkingOut = false;
      this.$emit('close');
    },
    openBasket() {
      if (!this.disableSidebar) {
        this.$refs.drawer.show();
        this.analyticEventStore.setOrganizationId(this.sessionStore.organizationId);
        this.analyticEventStore.logEvent({
          action: `gift-basket-sidebar-open`
        });
      }
    },
    toggleDrawerFullScreen(fullScreen) {
      this.$refs.drawer.toggleFullScreen(fullScreen);
    },

    // Reset the donation session to clear any cached data
    resetDonationSession() {
      this.sessionStore.donationSession.intent_id = null;
      this.sessionStore.donationSession.client_secret = null;
      this.sessionStore.donationSession.loaded = false;
    },

    // New Sign in Modal
    openSignInModal() {
      this.resetDonationSession();
      this.showSignInModal = true;
      this.analyticEventStore.logEvent({
        action: `gift-basket-sidebar-sign-in-open`
      });
    },

    openSignOutModal() {
      // Close the giving form drawer first so the confirmation modal isn't
      // hidden behind it (the drawer overlay sits at a higher stacking level).
      // See DONATE-1625.
      this.$refs.drawer.hide();
      this.userSignOutClicked = true;
    },

    signOutUser() {
      this.signingOut = true;
      this.userStore.signOut().then(() => {
        this.userSignedIn = false;
        this.sessionStore.resetState();
      });
      this.signedOut = true;
      this.analyticEventStore.logEvent({
        action: `checkout-flow-sign-out`
      });
    },

    closeSignOutModal() {
      this.userSignOutClicked = false;
    },

    // Populate donor info from user store if they are signed in
    populateDonorInfo(userData = null) {
      // If userData is provided, use it directly (from sign-in modal)
      if (userData && userData.donorEmail) {
        // Clear stale session data when switching users
        this.sessionStore.donationSession.intent_id = null;
        this.sessionStore.donationSession.client_secret = null;
        this.sessionStore.donationSession.checkout_token = null;
        this.sessionStore.donationSession.loaded = false;

        this.sessionStore.donationForm.fName = userData.fName || '';
        this.sessionStore.donationForm.lName = userData.lName || '';
        this.sessionStore.donationForm.donorEmail = userData.donorEmail || '';
        this.userSignedIn = true;
      } else if (this.userStore.user) {
        // Otherwise check if user is already signed in
        this.sessionStore.donationForm.fName = this.userStore.user.first_name || '';
        this.sessionStore.donationForm.lName = this.userStore.user.last_name || '';
        this.sessionStore.donationForm.donorEmail = this.userStore.user.email || '';
        this.userSignedIn = true;
      }
    },
  },

});

</script>

<template>
  <!--  GIFT BASKET BUTTON-->
  <div
    v-if="validProcessor"
    id="basket-button-container"
    :key="landingStore.localBrand"
    :class="{ 'has-items': sessionStore.donationCart && sessionStore.donationCart.length > 0 }"
    :style="cssProps"
    :title="sessionStore.donationCart && sessionStore.donationCart.length > 0 ? `You have ${sessionStore.donationCart.length} gift(s) in your basket` : 'Browse and add gifts'"
    @click="openBasket"
  >
    <i class="fa-solid fa-gift gift-icon" />
    <span class="button-label">
      {{ sessionStore.donationCart && sessionStore.donationCart.length > 0 ? 'Your Gifts' : 'Gifts' }}
    </span>
    <span
      v-if="sessionStore.donationCart && sessionStore.donationCart.length > 0"
      class="badge-count"
    >{{ sessionStore.donationCart.length }}</span>
  </div>

  <!--  Gift Basket Drawer-->
  <drawer
    ref="drawer"
    size="large"
    @hide="closeBasket"
  >
    <div
      class="h-full max-h-screen overflow-y-auto overflow-x-hidden"
      :style="cssProps"
    >
      <div
        class="m-2 md:m-4 flex flex-col sm:flex-row gap-x-12"
      >
        <div
          class="checkout-summary-container"
          :class="checkingOut ? 'w-full sm:w-1/3' : 'w-full'"
        >
          <!-- Summary List tiles -->
          <donation-checkout-summary
            v-if="hasDonationInCart"
            :remove-from-cart-enabled="!checkingOut"
            hide-tip
          >
            <template #donate-button-slot>
              <div
                v-if="!checkingOut"
                class="w-full flex flex-row justify-between bg-gray-100 p-4 border border-slate-300 rounded-md text-black"
              >
                <div class="text-md font-semibold">
                  Gift Subtotal
                </div>
                <div class="font-bold text-xl">
                  {{ ApplicationHelper.formatMoney(sessionStore.cartSubtotal) }}
                </div>
              </div>
              <button
                v-if="!checkingOut"
                class="p-3 mt-3"
                @click="checkout"
              >
                Give Now
              </button>
            </template>
          </donation-checkout-summary>

          <!--  Empty Basket TODO make prettier-->
          <div
            v-else
            class="flex flex-row mb-4 mt-4 text-black"
          >
            <i
              class="fa-solid fa-gift mr-4"
              style="font-size: 36px;"
              :style="{ color: landingStore.localBrand.color_primary_dark }"
            />
            <div
              class="flex flex-col"
            >
              <div class="title-regular">
                Your Gifts
              </div>
              <p>Your Gift Basket is Empty</p>
            </div>
          </div>
        </div>

        <div
          v-if="checkingOut"
          class="w-full sm:w-2/3 donor-info-form-container"
        >
          <div class="flex justify-between items-center">
            <div class="font-semibold text-2xl text-black">
              Donor Information
            </div>

            <!--   Sign In/Sign Out Button-->
            <div
              v-if="!userSignedIn"
              class="flex flex-col sm:flex-row items-end sm:items-center gap-0 sm:gap-2 whitespace-nowrap"
            >
              <span class="text-sm text-gray-600">Already have an account?</span>
              <span
                class="sign-in-link text-sm underline cursor-pointer text-gray-600 hover:text-gray-800"
                @click="openSignInModal"
              >
                Sign In
              </span>
            </div>
            <div
              v-else-if="userSignedIn"
              class="flex items-center gap-2"
            >
              <span
                class="sign-in-link text-sm underline cursor-pointer text-gray-600 hover:text-gray-800"
                @click="openSignOutModal"
              >
                Sign Out
              </span>
            </div>
          </div>

          <!--   Donor Info -->
          <div class="flex flex-row justify-between w-full mb-4 mt-3 ">
            <div class="w-1/2 mr-10">
              <label
                class="block text-sm mb-1"
              >{{ t('donation.f_name') }}</label>
              <input
                v-model="formData.fName"
                placeholder="John"
              >
              <span
                v-if="formErrors.fName"
                class="form-error"
              >
                {{ formErrors.fName }}
              </span>
            </div>
            <div class="w-1/2">
              <label
                class="block text-sm mb-1"
              >{{ t('donation.l_name') }}</label>
              <input
                v-model="formData.lName"
                placeholder="Smith"
              >
              <span
                v-if="formErrors.lName"
                class="form-error"
              >
                {{ formErrors.lName }}
              </span>
            </div>
          </div>

          <div class="mb-4 w-full">
            <label
              class="block text-sm mb-1"
            >{{ t('donation.email_address') }}</label>
            <input
              v-model="formData.donorEmail"
              type="email"
              placeholder="example@email.com"
            >
            <span
              v-if="formErrors.email"
              class="form-error"
            >
              {{ formErrors.email }}
            </span>
          </div>

          <div class="mb-4 w-full">
            <label
              class="block text-sm mb-1"
            >{{ t('donation.message') }}</label>
            <textarea
              v-model="formData.notes"
              rows="2"
              placeholder="Add a message to your donation"
            />
            <span
              v-if="formErrors.message"
              class="form-error"
            >
              {{ formErrors.message }}
            </span>
          </div>


          <div
            v-if="sessionStore.showTipOption"
            class="my-2"
          >
            <donation-tip-form />
          </div>

          <donation-checkout-total />
          <div
            class="mb-4"
          >
            <HelcimPaymentButton
              v-if="showHelcimPaymentButton"
              ref="paymentButton"
              :redirect="false"
              :user-signed-in="userSignedIn"
              :user-data="userSignedIn ? userStore.user : null"
              @payment-success="onPaymentSuccess"
              @payment-error="onPaymentError"
              @click="donateButtonClicked"
            />

            <StripePaymentButton
              v-if="showStripePaymentButton"
              ref="paymentButton"
              :redirect="false"
              @click="donateButtonClicked"
              @payment-success="onPaymentSuccess"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Sign In Modal - Show on top of drawer-->
    <SignInModal
      v-model="showSignInModal"
      class="modal-overlay"
      @user-signed-in="populateDonorInfo"
    />
  </drawer>

  <base-modal
    v-if="userSignOutClicked"
    title="Sign Out?"
    :background-dismissible="false"
    @close="closeSignOutModal"
  >
    <!--  Sign Out pop up confirmation-->
    <template #default>
      <!-- Show success message -->
      <div
        v-if="signedOut"
        class="m-20 text-blue-500 text-xl text-center"
      >
        Signing out!
        <LoadingSpinner size="small mt-2" />
      </div>
      <!-- Show loading spinner -->
      <!--      <div v-else-if="signingOut" class="flex items-center justify-center h-full">-->
      <!--        <LoadingSpinner size="small" />-->
      <!--      </div>-->

      <div v-else>
        <p>Are you sure you want to sign out?</p>
        <div class="mt-4 flex justify-end gap-2">
          <button
            class="btn-filled"
            @click="signOutUser"
          >
            Sign Out
          </button>
          <button
            class="btn-outline"
            @click="closeSignOutModal"
          >
            Cancel
          </button>
        </div>
      </div>
    </template>
  </base-modal>

  <popup
    ref="popup"
    :message="popupMessage"
    type="error"
    :duration="7000"
  />
</template>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';
#basket-button-container {
  @apply relative inline-flex flex-row items-center cursor-pointer;
  padding: 8px 16px;
  border-radius: 0.5rem;
  gap: 8px;
  background-color: var(--color-primary);
  color: var(--color-primary-text);

  :deep(.icon) svg path {
    fill: var(--color-primary-text);
  }

  .gift-icon {
    font-size: 18px;
    color: var(--color-primary-text);
  }

  &:hover {
    background-color: var(--color-primary-dark);
  }

  .button-label {
    @apply font-semibold text-sm whitespace-nowrap;
  }

  .badge-count {
    @apply inline-flex items-center justify-center font-bold;
    position: absolute;
    top: -8px;
    right: -8px;
    font-size: 0.65rem;
    min-width: 20px;
    height: 20px;
    padding: 0 4px;
    border-radius: 999px;
    background-color: var(--color-primary-text);
    color: var(--color-primary);
  }

  @screen md {
    padding: 10px 20px;
    gap: 10px;

    .button-label {
      @apply text-base;
    }
  }
}

.donor-info-form-container {
  @apply p-4 mt-4 border border-slate-500 rounded-md;

  @screen md {
    @apply p-8 mt-10;
  }
}

// Inputs
button {
  @apply w-full rounded font-semibold text-lg;

  background-color: var(--color-primary);
  color: var(--color-primary-text);

  &:hover {
    background-color: var(--button-hover);
  }
}


input, :deep(input), select {
  @apply bg-white text-black border border-gray-300 disabled:bg-default_white disabled:bg-opacity-0;
  @apply w-full px-4 py-2 text-lg leading-normal rounded-lg shadow-sm focus:outline-none max-h-12;

  &:focus {
    border: 1px solid var(--input-focus)
  }
}

textarea, :deep(textarea) {
  @apply bg-white text-black border border-gray-300 disabled:bg-default_white disabled:bg-opacity-0;
  @apply w-full px-4 py-2 text-lg leading-normal rounded-lg shadow-sm focus:outline-none;

  &:focus {
    border: 1px solid var(--input-focus)
  }
}

label {
  color: black;
}

.form-error {
  @apply text-sm text-red-500 font-semibold;
}

.modal-overlay {
  z-index: 9999 !important;
}

.sign-in-btn {
  background-color: transparent;
  border: 1px solid var(--color-primary);
  color: var(--color-primary);

  &:hover {
    background-color: var(--color-primary);
    color: var(--color-primary-text);
  }
}
</style>
